import { useEffect, useRef, useState } from 'react'
import { Map, View, Feature } from 'ol'
import TileLayer from 'ol/layer/Tile'
import OSM from 'ol/source/OSM'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { Draw } from 'ol/interaction'
import GeoJSON from 'ol/format/GeoJSON'
import { fromLonLat } from 'ol/proj'
import { Style, Stroke, Circle as CircleStyle, Fill } from 'ol/style'
import 'ol/ol.css'

interface Props {
  value?: string | null                 // GeoJSON (FeatureCollection lub legacy geometry)
  onChange: (geojson: string | null) => void
  water: boolean
  sewer: boolean
  height?: number
}

const COLORS: Record<string, string> = { water: '#2563eb', sewer: '#d97706', point: '#16a34a' }

const styleFor = (feature: Feature): Style => {
  const medium = (feature.get('medium') as string) || 'water'
  if (feature.getGeometry()?.getType() === 'Point') {
    return new Style({
      image: new CircleStyle({ radius: 7, fill: new Fill({ color: COLORS.point }), stroke: new Stroke({ color: '#fff', width: 2 }) }),
    })
  }
  return new Style({ stroke: new Stroke({ color: COLORS[medium] || COLORS.water, width: 4 }) })
}

// Wczytuje istniejącą geometrię (FeatureCollection / Feature / bare geometry) do źródła
function loadInto(source: VectorSource, value?: string | null) {
  if (!value) return
  let obj: any
  try { obj = JSON.parse(value) } catch { return }
  const fmt = new GeoJSON()
  const opts = { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' } as any
  try {
    let feats: Feature[] = []
    if (obj.type === 'FeatureCollection') feats = fmt.readFeatures(obj, opts) as Feature[]
    else if (obj.type === 'Feature') feats = [fmt.readFeature(obj, opts) as Feature]
    else if (obj.type) feats = [new Feature({ geometry: (fmt as any).readGeometry(obj, opts) })]
    source.addFeatures(feats)
  } catch { /* ignore malformed */ }
}

export default function RealizationSketchMap({ value, onChange, water, sewer, height = 320 }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const mapRef = useRef<Map | null>(null)
  const sourceRef = useRef<VectorSource | null>(null)
  const drawRef = useRef<Draw | null>(null)
  const [mode, setMode] = useState<null | 'water' | 'sewer' | 'point'>(null)

  // Serializuje aktualne cechy do FeatureCollection (EPSG:4326) i zgłasza zmianę
  const emit = () => {
    const src = sourceRef.current
    if (!src) return
    const fmt = new GeoJSON()
    const feats = src.getFeatures()
    if (feats.length === 0) { onChange(null); return }
    const fc = fmt.writeFeaturesObject(feats, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' } as any)
    onChange(JSON.stringify(fc))
  }

  useEffect(() => {
    if (!ref.current) return
    const source = new VectorSource()
    loadInto(source, value)
    const vector = new VectorLayer({ source, style: (f) => styleFor(f as Feature) })
    const map = new Map({
      target: ref.current,
      layers: [new TileLayer({ source: new OSM({ maxZoom: 19 }) }), vector],
      view: new View({ center: fromLonLat([20.83, 51.99]), zoom: 13 }),
    })
    // Dopasuj widok do istniejących cech
    const extent = source.getExtent()
    if (source.getFeatures().length > 0 && extent && isFinite(extent[0])) {
      map.getView().fit(extent, { padding: [40, 40, 40, 40], maxZoom: 18 })
    }
    mapRef.current = map
    sourceRef.current = source
    return () => { map.setTarget(undefined); mapRef.current = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setDraw = (m: null | 'water' | 'sewer' | 'point') => {
    const map = mapRef.current, source = sourceRef.current
    if (!map || !source) return
    if (drawRef.current) { map.removeInteraction(drawRef.current); drawRef.current = null }
    if (m === null || m === mode) { setMode(null); return }
    const draw = new Draw({ source, type: m === 'point' ? 'Point' : 'LineString' })
    draw.on('drawend', (e: any) => {
      e.feature.set('medium', m)
      // wyłącz tryb po narysowaniu jednego elementu
      setTimeout(() => { if (drawRef.current) { map.removeInteraction(drawRef.current); drawRef.current = null } setMode(null); emit() }, 0)
    })
    map.addInteraction(draw)
    drawRef.current = draw
    setMode(m)
  }

  const clearAll = () => {
    sourceRef.current?.clear()
    emit()
  }
  const undoLast = () => {
    const src = sourceRef.current
    if (!src) return
    const feats = src.getFeatures()
    if (feats.length) { src.removeFeature(feats[feats.length - 1]); emit() }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {water && <ModeBtn active={mode === 'water'} color={COLORS.water} onClick={() => setDraw('water')}>💧 Rysuj wodę</ModeBtn>}
        {sewer && <ModeBtn active={mode === 'sewer'} color={COLORS.sewer} onClick={() => setDraw('sewer')}>🚿 Rysuj kanał</ModeBtn>}
        <ModeBtn active={mode === 'point'} color={COLORS.point} onClick={() => setDraw('point')}>📍 Budynek</ModeBtn>
        <button onClick={undoLast} className="min-h-[36px] px-3 rounded-lg text-sm bg-gray-100 text-gray-700 hover:bg-gray-200">↩ Cofnij</button>
        <button onClick={clearAll} className="min-h-[36px] px-3 rounded-lg text-sm bg-red-50 text-red-700 hover:bg-red-100">Wyczyść</button>
      </div>
      <div ref={ref} style={{ height }} className="w-full rounded-lg overflow-hidden border border-gray-300 bg-gray-100" />
      <p className="mt-1 text-[11px] text-gray-400">
        {mode ? 'Klikaj punkty linii na mapie; dwuklik kończy rysowanie.' : 'Wybierz co rysujesz i naszkicuj przebieg na mapie.'}
      </p>
    </div>
  )
}

function ModeBtn({ active, color, onClick, children }: { active: boolean; color: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className="min-h-[36px] px-3 rounded-lg text-sm font-medium border"
      style={active ? { backgroundColor: color, color: '#fff', borderColor: color } : { backgroundColor: '#fff', color: '#374151', borderColor: '#d1d5db' }}>
      {children}
    </button>
  )
}
